import { isRoomJoined } from '../../room';
import { MEDIA_TYPE } from '../media';
import { getBridgeSession, getLocalIDMember } from '../members';
import { StateListenerRegistry } from '../redux';
import ReducerRegistry from '../redux/ReducerRegistry';
import { TRACK_ADDED, TRACK_REMOVED, TRACK_UPDATED } from './actionTypes';
import { getTrackState, isTrackMutedByMediaType } from './functions';
/**
 * Track type.
 *
 * @typedef {object} Track
 * @property {JitsiLocalTrack|JitsiRemoteTrack} track - The associated
 * {@code track} instance. Optional for local tracks if those are still
 * being created (ie {@code getUserMedia} is still in progress).
 * @property {Promise} [gumProcess] - If a local track is still being created,
 * it will have no {@code track}, but a {@code gumProcess} set to a
 * {@code Promise} with and extra {@code cancel()}.
 * @property {boolean} local=false - If the track is local.
 * @property {MEDIA_TYPE} mediaType=false - The media type of the track.
 * @property {boolean} mirror=false - The indicator which determines whether the
 * display/rendering of the track should be mirrored. It only makes sense in the
 * context of video (at least at the time of this writing).
 * @property {boolean} muted=false - If the track is muted.
 * @property {(string|undefined)} participantId - The ID of the participant whom
 * the track belongs to.
 * @property {boolean} videoStarted=false - If the video track has already
 * started to play.
 * @property {(VIDEO_TYPE|undefined)} videoType - The type of video track if
 * any.
 */
/**
 * Reducer function for a single track.
 *
 * @param {Track|undefined} state - Track to be modified.
 * @param {Object} action - Action object.
 * @param {string} action.type - Type of action.
 * @param {string} action.name - Name of last media event.
 * @param {string} action.newValue - New participant ID value (in this
 * particular case).
 * @param {string} action.oldValue - Old participant ID value (in this
 * particular case).
 * @param {Track} action.track - Information about track to be changed.
 * @param {Participant} action.participant - Information about participant.
 * @returns {Track|undefined}
 */
function track(state, action) {
    switch (action.type) {
        case TRACK_UPDATED: {
            const t = action.track;
            if (state.track === t.track) {
                for (const p in t) {
                    // @ts-ignore
                    if (state[p] !== t[p]) {
                        return {
                            ...state,
                            ...t,
                        };
                    }
                }
            }
            break;
        }
    }
    return state;
}
/**
 * Listen for actions that mutate (e.g. Add, remove) local and remote tracks.
 */
ReducerRegistry.register('features/base/tracks', (state = [], action) => {
    switch (action.type) {
        case TRACK_UPDATED:
            return state.map((t) => track(t, action));
        case TRACK_ADDED: {
            let withoutTrackStub = state;
            if (action.track.local) {
                withoutTrackStub = state.filter((t) => !t.local || t.mediaType !== action.track.mediaType);
            }
            return [...withoutTrackStub, action.track];
        }
        case TRACK_REMOVED:
            return state.filter((t) => t.track !== action.track.track);
        default:
            return state;
    }
});
// ★BridgeSession.Set 핑퐁 폭풍 수정(2026-08-05 서버 감사: 초당 ~70건, 21분 87k건).
// 종전 결함 3중주: ①재전송 가드가 존재하지 않는 필드(bridge.isAudioMuted — 실제 필드는
// audio_muted) 비교라 항상 참 = 수신마다 무조건 재전송 ②수신한 bridge 를 id 포함 그대로
// 스프레드해 되쏨 — 같은 계정 두 탭이 서로의 세션 id 를 교대 재방출 ③종료 조건 없음.
// 수정 설계: payload 는 항상 "내" jitsi 세션 id 로 구성하고, 내 멤버 엔트리를 다른 세션이
// 덮은 상태에서는 에코하지 않으며, 같은 교정값의 반복 송신(구버전 탭 등 타 발신자와의
// 루프 신호)은 상한 후 백오프한다. 정당 송신 경로: join 재단언(conference.js S1)·발언 허가
// (_updateBridgeAudioTrackId S2)는 이 리스너와 별개로 즉시 송신된다.
const BRIDGE_CORRECTION_LIMIT = 3; // 같은 교정값 연속 송신 상한
const BRIDGE_CORRECTION_BACKOFF_MS = 30000;
const BRIDGE_SEND_DEBOUNCE_MS = 300; // 연속 mute 토글/이벤트 폭주 합치기(trailing)
let bridgeCorrectionKey = '';
let bridgeCorrectionCount = 0;
let bridgeCorrectionMutedUntil = 0;
let bridgeSendTimer = null;
StateListenerRegistry.register((state) => {
    if (!isRoomJoined(state))
        return false;
    return {
        bridge: getBridgeSession(state, getLocalIDMember(state)),
        isAudioMuted: isTrackMutedByMediaType(state, 'local', MEDIA_TYPE.AUDIO),
        isVideoMuted: isTrackMutedByMediaType(state, 'local', MEDIA_TYPE.VIDEO),
    };
}, (result, store) => {
    if (!result)
        return;
    else if (!result.bridge)
        return;
    // 내 jitsi 세션 id — 미참여/재접속 창에는 송신 보류(재접속 후 join 이 전체 재단언).
    // ★라이브 회의 객체(jitsi.Conference.conference)에서 읽는다: 재접속 중에는 null 이라 자연히 보류된다.
    //   Management.jitsiConference 는 나간 구 회의 객체가 남아 구 id 를 돌려줄 수 있어 쓰지 않는다
    //   (그 구 id 를 스탬프해 보내면 학생 매핑이 신/구 id 사이를 핑퐁하는 루프의 씨앗이 된다 — 2026-09-21 실측).
    const myId = APP.management?.jitsi?.Conference?.conference?.myUserId?.();
    if (!myId)
        return;
    // 다른 세션(다른 탭)의 bridge 가 내 멤버 엔트리를 덮은 상태 — 에코 금지.
    // (중복 접속은 미지원 정책: 서버 레코드는 마지막 송신 세션 기준으로 둔다)
    if (result.bridge.id && result.bridge.id !== myId)
        return;
    // 서버 레코드(내 세션)와 로컬 진실이 일치하면 송신 불필요 — 교정 이력도 리셋
    if (result.bridge.audio_muted === result.isAudioMuted &&
        result.bridge.video_muted === result.isVideoMuted) {
        bridgeCorrectionKey = '';
        bridgeCorrectionCount = 0;
        return;
    }
    const payload = {
        ...result.bridge,
        id: myId,
        audio_muted: result.isAudioMuted,
        video_muted: result.isVideoMuted,
    };
    const key = JSON.stringify(payload);
    const now = Date.now();
    if (now < bridgeCorrectionMutedUntil)
        return; // 백오프 중
    if (key === bridgeCorrectionKey) {
        // 같은 교정을 보냈는데 서버 레코드가 계속 되돌아옴 = 다른 발신자와의 핑퐁 신호
        bridgeCorrectionCount += 1;
        if (bridgeCorrectionCount >= BRIDGE_CORRECTION_LIMIT) {
            bridgeCorrectionMutedUntil = now + BRIDGE_CORRECTION_BACKOFF_MS;
            bridgeCorrectionCount = 0;
            console.warn('[bridge] 동일 교정 반복 — 중복 세션 핑퐁 의심, 30초 백오프');
            return;
        }
    }
    else {
        bridgeCorrectionKey = key;
        bridgeCorrectionCount = 1;
    }
    if (bridgeSendTimer)
        clearTimeout(bridgeSendTimer);
    bridgeSendTimer = setTimeout(() => {
        // 디바운스 대기 중 퇴장했으면 송신하지 않는다(닫힌 WS 로의 유령 송신 방지)
        if (!isRoomJoined(APP.store.getState))
            return;
        APP.management.setBridgeId(payload);
    }, BRIDGE_SEND_DEBOUNCE_MS);
}, {
    deepEquals: true,
});
StateListenerRegistry.register((state) => isTrackMutedByMediaType(state, 'local', MEDIA_TYPE.VIDEO), (isVideoMuted, store) => {
    if (!isRoomJoined(store.getState))
        return;
    APP.management.conferenceMutedVideoTrack(isVideoMuted);
}, {
    deepEquals: true,
});
StateListenerRegistry.register((state) => {
    const track = getTrackState(state).find(t => t.local && t.mediaType === MEDIA_TYPE.VIDEO);
    return track?.deviceId ? track.deviceId : track?.track?.deviceId;
}, 
// throttle(
(deviceId, store) => {
    if (!isRoomJoined(store.getState))
        return;
    try {
        deviceId && APP.management.conferenceReplaceTrack(deviceId);
    }
    catch (e) {
        console.error(e);
    }
}, {
    deepEquals: true,
});
// StateListenerRegistry.register(
// 	(state: IStore['getState']) => {
// 		const remote = getLayoutMemberByType(state, 'remote');
// 		if (!remote) return;
// 		const video_muted_list = remote.map(r => {
// 			const bridge_sessions = getBridgeSession(state, r);
// 			return {
// 				user_uuid: r,
// 				video_muted: bridge_sessions?.video_muted,
// 			};
// 		});
// 		return video_muted_list;
// 	},
// 	(result, store, prevResult) => {
// 		if (!result) return;
// 		if (!isRoomJoined(store.getState)) return;
// 		result.forEach((r: any) => {
// 			const prev = prevResult?.find((p: any) => p.user_uuid === r.user_uuid);
// 			console.log(r, prev);
// 			if (!prev || prev.video_muted !== r.video_muted) {
// 				APP.management.conferenceMutedVideoTrack(r.user_uuid, r.video_muted);
// 			}
// 		});
// 	},
// 	{
// 		deepEquals: true,
// 	},
// );
